import {
  AppVersion,
  Button,
  ButtonColor, Colors,
  getMediaDevices,
  PopupButtonType,
  PopupIconType,
  PopupOnCloseData,
  PopupRef,
  showPopup
} from "@kalyzee/kast-web-sdk/web-components";
import {
  SettingsForm,
  SettingsFormRef,
  SettingsFormChangeData,
} from "@kalyzee/kast-app-web-components";
import React, { createRef, ForwardedRef, useImperativeHandle, useRef } from "react";
import { Settings } from "../../helpers/settings";
import { useSettings } from "../../hooks/settings";
import { useRequestAdminPin } from "../../hooks/utils";
import {
  settingsSchema,
  SETTINGS_SCHEMA_VERSION,
  exportSettings,
  importSettings,
  prepareSettingsForForm,
  restoreSettingsFromForm,
} from "../../helpers/settingsSchema";
import styles from "./SettingsPopup.module.css";
import Versions from "./Versions";

export interface SettingsPopupProps {}

export interface SettingsPopupRef {
  openPopup: () => void;
}

const SettingsPopup = React.forwardRef(({}: SettingsPopupProps, forwardRef: ForwardedRef<SettingsPopupRef | undefined>) => {
  const [settings, saveSettings] = useSettings();
  const requestAdminPin = useRequestAdminPin();
  const isOpened = useRef<boolean>(false);

  const tryOpenPopup = () => {
    requestAdminPin((success) => {
      if (success) openPopup();
    });
  };

  const showErrorPopup = (title: string, content: React.ReactNode) => {
    showPopup({
      title,
      iconTitle: PopupIconType.ERROR,
      content,
      buttons: [{ type: PopupButtonType.OK, element: "OK" }],
    });
  };

  const showWarningsPopup = (title: string, warnings: string[], onContinue: () => void) => {
    showPopup({
      title,
      iconTitle: PopupIconType.WARNING,
      content: (
        <div className={styles.warningsContent}>
          <div className={styles.warningsList}>
            {warnings.map((w, i) => (
              <div key={i} className={styles.warningItem}>• {w}</div>
            ))}
          </div>
        </div>
      ),
      buttons: [
        { type: PopupButtonType.CANCEL, element: "Annuler" },
        {
          type: PopupButtonType.VALIDATE,
          element: <div style={{ color: Colors.getMainGreen() }}>Continuer quand même</div>,
        },
      ],
      onClose: (data: PopupOnCloseData) => {
        if (data.closeFromButton?.type === PopupButtonType.VALIDATE) {
          onContinue();
        }
      },
    });
  };

  const doSave = (settingsFormRef: React.RefObject<SettingsFormRef | null>) => {
    let newSettings: Settings | undefined;
    let error: string | undefined;
    try {
      const jsonStr = settingsFormRef.current?.getJsonString() ?? '{}';
      const parsed = JSON.parse(jsonStr);
      const restored = restoreSettingsFromForm(parsed, settingsSchema);
      newSettings = saveSettings(restored);
    } catch (err) {
      error = (err as any)?.message;
    }

    if (newSettings) {
      showPopup({
        title: "Appliquer les changements",
        iconTitle: PopupIconType.SUCCESS,
        content: "Vous avez modifié les paramètres. Voulez-vous rafraîchir la page pour appliquer tout de suite vos modifications ?",
        buttons: [
          { type: PopupButtonType.CANCEL, element: "Non" },
          { type: PopupButtonType.VALIDATE, element: <div style={{ color: Colors.getMainGreen() }}>Oui</div> },
        ],
        onClose: (data: PopupOnCloseData) => {
          if (data.closeFromButton?.type === PopupButtonType.VALIDATE) {
            window.location.reload();
          }
        },
      });
    } else {
      showErrorPopup(
        "Impossible de sauvegarder les paramètres",
        "Une erreur est apparue durant la sauvegarde des paramètres : " + error,
      );
    }
  };

  const openPopup = () => {
    if (isOpened.current) return;
    isOpened.current = true;

    const settingsFormRef = createRef<SettingsFormRef>();
    const popupRef = createRef<PopupRef>();
    const fileInputRef = createRef<HTMLInputElement>();

    let formValid = true;
    let formErrors: { path: string; message: string }[] = [];

    const handleFormChange = (data: SettingsFormChangeData) => {
      formValid = data.valid;
      formErrors = data.errors;
    };

    const trySave = (): boolean => {
      if (!formValid) {
        showErrorPopup(
          "Impossible de sauvegarder",
          <div className={styles.warningsContent}>
            <div style={{ marginBottom: 8 }}>Corrigez les erreurs suivantes :</div>
            <div className={styles.warningsList}>
              {formErrors.map((e, i) => (
                <div key={i} className={styles.warningItem}>• <strong>{e.path}</strong>: {e.message}</div>
              ))}
            </div>
          </div>,
        );
        return false;
      }
      return true;
    };

    const handleExport = () => {
      try {
        const currentValue = settingsFormRef.current?.getValue() ?? {};
        exportSettings(restoreSettingsFromForm(currentValue, settingsSchema));
      } catch (err) {
        showErrorPopup("Erreur d'export", "Impossible d'exporter les paramètres : " + (err as any)?.message);
      }
    };

    const handleImportClick = () => {
      fileInputRef.current?.click();
    };

    const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      // Reset input so the same file can be re-imported
      e.target.value = '';

      try {
        const result = await importSettings(file);
        const allWarnings: string[] = [];
        if (result.versionWarning) allWarnings.push(result.versionWarning);
        allWarnings.push(...result.warnings);

        const applyImport = () => {
          settingsFormRef.current?.setValue(JSON.stringify(result.settings));
        };

        if (allWarnings.length > 0) {
          showWarningsPopup("Avertissements à l'import", allWarnings, applyImport);
        } else {
          applyImport();
        }
      } catch (err) {
        showErrorPopup("Erreur d'import", (err as Error).message);
      }
    };

    showPopup({
      title: (
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          Paramètres
          <span style={{ fontSize: '0.65em', opacity: 0.5 }}>v{SETTINGS_SCHEMA_VERSION}</span>
        </div>
      ),
      style: { width: "70%", maxHeight: "85vh" },
      enableBackdropDismiss: false,
      content: (
        <div className={styles.popupSettingsContent}>
          <div className={styles.popupSettingsButtonsContainer}>
            <Button
              color={ButtonColor.PURPLE}
              title="Media Devices"
              className={styles.button}
              onPress={async () => {
                const devices = await getMediaDevices("both", "input");
                showPopup({
                  title: "Devices",
                  content: <div className={styles.popupDevicesContent}>{JSON.stringify(devices, null, 4)}</div>,
                  buttons: [{ type: PopupButtonType.OK, element: "OK" }],
                });
              }}
            />
            <Button
              title="Exporter JSON"
              className={styles.button}
              style={{ backgroundColor: Colors.getPickledBluewood() }}
              onPress={handleExport}
            />
            <Button
              title="Importer JSON"
              className={styles.button}
              style={{ backgroundColor: Colors.getPickledBluewood() }}
              onPress={handleImportClick}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              style={{ display: 'none' }}
              onChange={handleFileSelected as any}
            />
          </div>
          <div className={styles.settingsFormContainer}>
            <SettingsForm
              ref={settingsFormRef}
              schema={settingsSchema}
              defaultValue={JSON.stringify(prepareSettingsForForm(settings, settingsSchema))}
              onChange={handleFormChange}
              style={{ width: '100%' }}
            />
          </div>
          <Versions />
          <AppVersion />
        </div>
      ),
      buttons: [
        {
          type: PopupButtonType.CANCEL,
          element: "Fermer",
          onClick: () => {
            // Intercept close: ask if user wants to save
            showPopup({
              title: "Sauvegarder avant de quitter ?",
              content: "Vous avez peut-être des modifications non sauvegardées. Voulez-vous sauvegarder avant de fermer ?",
              buttons: [
                { type: PopupButtonType.CANCEL, element: "Non, fermer" },
                {
                  type: PopupButtonType.VALIDATE,
                  element: <div style={{ color: Colors.getMainGreen() }}>Sauvegarder</div>,
                },
              ],
              onClose: (confirmData: PopupOnCloseData) => {
                if (confirmData.closeFromButton?.type === PopupButtonType.VALIDATE) {
                  if (trySave()) {
                    popupRef.current?.close();
                    doSave(settingsFormRef);
                  }
                } else {
                  popupRef.current?.close();
                }
              },
            });
            return false; // prevent default close
          },
        },
        {
          type: PopupButtonType.VALIDATE,
          element: <div style={{ color: Colors.getMainGreen() }}>Sauvegarder</div>,
          onClick: () => trySave(),
        },
      ],
      onClose: (data: PopupOnCloseData) => {
        isOpened.current = false;
        if (data.closeFromButton?.type === PopupButtonType.VALIDATE) {
          doSave(settingsFormRef);
        }
      },
    }, popupRef);
  };

  useImperativeHandle(forwardRef, () => ({
    openPopup: tryOpenPopup,
  }));

  return null;
});

export default SettingsPopup;
